import { action, computed, makeObservable, observable } from 'mobx'

import { ISharedSitePermit } from '~/client/graph/types.generated'
import DefaultApps from '~/client/src/shared/enums/DefaultApps'
import { ICalendarDates } from '~/client/src/shared/models/CalendarEvent'
import ILogisticItem, {
  LogisticItemApp,
} from '~/client/src/shared/models/ILogisticItem'
import SitePermit from '~/client/src/shared/models/Permit'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import FormCategoriesStore from '~/client/src/shared/stores/domain/FormCategories.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import BaseLogisticsStore from '~/client/src/shared/stores/ui/BaseLogistics.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NOOP } from '~/client/src/shared/utils/noop'

import MobileEventStore from '../../../stores/EventStore/MobileEvents.store'
import MobileInitialState from '../../../stores/MobileInitialState'

export enum LogisticsPageViewMode {
  Map = 'map',
  List = 'list',
  Calendar = 'calendar',
}

export default class MobileLogisticsStore extends BaseLogisticsStore {
  @observable public selectedViewMode: LogisticsPageViewMode =
    LogisticsPageViewMode.List
  @observable public viewingDate: Date = new Date()
  @observable public isWorkflowViewFormShown: boolean = false
  @observable public workflowsLoading: boolean = true

  @observable private selectedWorkflow: SitePermit = null
  @observable private placeholderStartDate: Date
  @observable private placeholderEndDate: Date
  @observable private _allForms: SitePermit[] = []

  public constructor(
    protected readonly projectDateStore: ProjectDateStore,
    protected readonly eventsStore: MobileEventStore,
    protected readonly sitemapsStore: SitemapsStore,
    protected readonly globeViewsStore: GlobeViewsStore,
    protected readonly tilesetsStore: TilesetsStore,
    closuresStore: ClosuresStore,
    permitTypesStore: PermitTypesStore,
    formCategoriesStore: FormCategoriesStore,
    locationAttributesStore: LocationAttributesStore,
    companiesStore: CompaniesStore,
    sharedSitePermitStore: SharedSitePermitStore,
  ) {
    super(
      projectDateStore,
      eventsStore,
      sitemapsStore,
      globeViewsStore,
      permitTypesStore,
      formCategoriesStore,
      locationAttributesStore,
      companiesStore,
      closuresStore,
      sharedSitePermitStore,
    )
    makeObservable(this)

    this.selectedViewMode =
      (localStorage.getItem(
        DefaultApps.formsViewKey,
      ) as LogisticsPageViewMode) || LogisticsPageViewMode.List
  }

  public clearPostEventCallback: () => void = NOOP

  public initPostEventCallback = (): void => {
    this.clearPostEventCallback = this.eventsStore.addPostEventCallback(
      this.onEvent,
    )
  }

  @action.bound
  public onEvent(eventContext: EventContext): void {
    const [eventType] = eventContext.event

    switch (eventType) {
      case e.SITEMAPS_RECEIVED:
      case e.SITEMAP_UPDATED:
      case e.GLOBE_VIEWS_RECEIVED:
      case e.GLOBE_VIEW_UPDATED:
      case e.GLOBE_VIEW_ITEM_DATA_UPDATED:
      case e.SITEMAP_ITEM_DATA_UPDATED:
      case e.LOGISTICS_CONFIGURATIONS_RECEIVED:
      case e.LOGISTICS_CONFIGURATIONS_UPDATED:
      case e.WORKFLOWS_CONFIGURATIONS_RECEIVED:
      case e.WORKFLOWS_CONFIGURATIONS_UPDATED:
        this.ensureCorrectViewMode()
    }
  }

  @action.bound
  public ensureCorrectViewMode(): void {
    if (
      this.selectedViewMode === LogisticsPageViewMode.Map &&
      this.isMapViewDisabled &&
      !this.isLoaderShown
    ) {
      this.setViewMode(LogisticsPageViewMode.List)
    }
  }

  public convertEntitiesToLogistics = (
    forms: SitePermit[],
    sharedForms: ISharedSitePermit[] = [],
  ): ILogisticItem[] => {
    const _forms = forms.map<ILogisticItem>(form => ({
      id: form.id,
      entityId: form.id,
      app: LogisticItemApp.FORM,
      type: form.getTypeOfPermitType(this.permitTypesStore),
      name: form.getCaption(this.permitTypesStore),
      locations: this.getLocationsFromIds(form.locations.map(l => l.id)),
      equipment: this.getLocationsFromIds(form.equipment.map(l => l.id)),
      status: form.status,
      isStatusLate: form.isLate,
      workflowStepLevel: form.workflowStepLevel,
      companyIds: form.companyIds || [],
      startDate: form.startDate,
      endDate: form.endDate,
      responsibleContactIds: form.requesterIds || [],
      formCategoryId: form.categoryId || '',
      original: form,
    }))

    const _sharedForms = sharedForms
      .map(f => SitePermit.fromSharedPermitDto(f))
      .map<ILogisticItem>(form => ({
        id: form.id,
        entityId: form.id,
        app: LogisticItemApp.FORM,
        type: form.getTypeOfPermitType(this.permitTypesStore),
        name: form.getCaption(this.permitTypesStore),
        locations: this.getLocationsFromIds(form.locations.map(l => l.id)),
        equipment: this.getLocationsFromIds(form.equipment.map(l => l.id)),
        status: form.status,
        isStatusLate: form.isLate,
        workflowStepLevel: form.workflowStepLevel,
        companyIds: form.companyIds || [],
        startDate: form.startDate,
        endDate: form.endDate,
        responsibleContactIds: form.requesterIds || [],
        formCategoryId: form.categoryId || '',
        original: form,
      }))

    return [..._forms, ..._sharedForms]
  }

  @computed
  public get calendarEventDates(): ICalendarDates {
    if (this.placeholderStartDate && this.placeholderEndDate) {
      return {
        startDate: this.placeholderStartDate,
        endDate: this.placeholderEndDate,
      }
    }
    return null
  }

  public get startDate(): Date {
    return this.projectDateStore.startOfDay(this.viewingDate)
  }

  public get endDate(): Date {
    return this.projectDateStore.endOfDay(this.viewingDate)
  }

  public get filters(): IFilters {
    return this.state.formsFilters
  }

  @action.bound
  public setViewingDate(value: Date) {
    this.viewingDate = value
  }

  @action.bound
  public setViewMode(value: LogisticsPageViewMode) {
    this.selectedViewMode = value
    localStorage.setItem(DefaultApps.formsViewKey, value)
    window.location.hash = value
  }

  @action.bound
  public setPlaceholderStartDate(value: Date) {
    this.placeholderStartDate = value
  }

  @action.bound
  public setPlaceholderEndDate(value: Date) {
    this.placeholderEndDate = value
  }

  @action.bound
  public resetPlaceholderDates() {
    this.placeholderStartDate = null
    this.placeholderEndDate = null
  }

  @computed
  public get allForms(): SitePermit[] {
    return this._allForms
  }

  @action.bound
  public setAllForms(forms: SitePermit[]) {
    this._allForms = forms
  }

  @action.bound
  public setWorkflowsLoading(loading: boolean) {
    this.workflowsLoading = loading
  }

  @action.bound
  public nextDate() {
    const { addDays, startOfDay } = this.projectDateStore
    this.setViewingDate(startOfDay(addDays(this.viewingDate, 1)))
  }

  @action.bound
  public prevDate() {
    const { addDays, startOfDay } = this.projectDateStore
    this.setViewingDate(startOfDay(addDays(this.viewingDate, -1)))
  }

  public get isLoaderShown(): boolean {
    const { workflows } = this.state
    const isBaseLoaderShown =
      !this.sitemapsStore.isDataReceived ||
      !this.closuresStore.isDataReceived ||
      !this.globeViewsStore.isDataReceived ||
      !(this.state.isTilesetsDisabled || this.tilesetsStore.isDataReceived)

    return isBaseLoaderShown || !workflows.isDataReceived
  }

  public get state(): MobileInitialState {
    return this.eventsStore.appState
  }

  @computed
  public get displayedWorkflow(): SitePermit {
    if (!this.selectedWorkflow) return null

    const formFromLocal = this._allForms.find(
      f => f.id === this.selectedWorkflow.id,
    )
    return formFromLocal?.copy() || this.selectedWorkflow.copy()
  }

  @action.bound
  public openWorkflowViewForm(permit: SitePermit) {
    this.selectedWorkflow = permit
    this.isWorkflowViewFormShown = true
  }
}
